import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReportNewsItem, ReportNewsResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { STATUS_SAID, when } from "../account/Reports.js";

export const REPORT_NEWS_KEY = ["report-news"] as const;

/**
 * What Joogoos said on this workspace's reports, on a bell beside the credit
 * ([ADR 0024](../../../../docs/adr/0024-a-report-is-filed-from-where-the-problem-is-and-fixed-means-a-build.md), amended
 * 2026-10-05). It replaced the customer's emails — the receipt, every answer and move, the release that fixed it — which
 * arrived three to a report and taught an inbox to skip them. The email is still there for whoever switches it on in
 * Settings; this is there for everybody.
 *
 * **Nothing is drawn until there is something to show**, the rule the activity indicator beside it keeps (27): a
 * workspace that never reported anything has no bell. Once Joogoos has said something it stays, quiet, so the last
 * answer can be found again; a coral count while any of it is new. Opening it is the looking: the count goes, and the
 * dots stay on what was new until the list is read again.
 *
 * Asked when the app opens and when the tab is come back to, never on a timer: a report moves a few times a week.
 */
export const useReportNews = () =>
  useQuery({ queryKey: REPORT_NEWS_KEY, queryFn: api.reportNews, staleTime: 60_000, refetchOnWindowFocus: true });

export function ReportNews({ onOpen, onAll, onSettings }: {
  onOpen: (reportId: number) => void;
  onAll: () => void;
  onSettings: () => void;
}) {
  const qc = useQueryClient();
  const news = useReportNews().data;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!news?.items.length) return null;
  const unseen = news.unseen;

  const toggle = () => {
    const opening = !open;
    setOpen(opening);
    if (!opening || unseen === 0) return;
    // Not a mutation: every write re-asks the balance and the activity (`queryClient.ts`), and looking spends nothing.
    qc.setQueryData<ReportNewsResponse>(REPORT_NEWS_KEY, (d) => (d ? { ...d, unseen: 0 } : d));
    api.seeReportNews().catch(() => { void qc.invalidateQueries({ queryKey: REPORT_NEWS_KEY }); });
  };
  const go = (f: () => void) => () => { setOpen(false); f(); };

  return (
    <div className="relative">
      <button
        onClick={toggle}
        aria-label={unseen ? `Your reports: ${unseen} new` : "Your reports"}
        aria-expanded={open}
        title="What Joogoos said on your reports"
        className="relative w-8 h-8 grid place-items-center rounded-full text-muted hover:text-ink hover:bg-page"
      >
        <Icon name="bell" size={17} />
        {unseen ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-coral text-white text-[10px] font-bold leading-4 text-center tabular-nums" aria-hidden>
            {unseen > 9 ? "9+" : unseen}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="region" aria-label="What Joogoos said on your reports"
            className="absolute right-0 top-10 z-50 w-80 max-w-[calc(100vw-1rem)] bg-panel border border-line rounded-2xl shadow-[0_14px_40px_rgba(23,23,26,.16)] overflow-hidden"
          >
            <p className="px-3.5 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-faint">Your reports</p>
            <ul className="max-h-[60vh] overflow-y-auto">
              {news.items.map((item, i) => (
                <li key={`${item.reportId}-${item.at}-${i}`}>
                  <button onClick={go(() => onOpen(item.reportId))} className="w-full flex items-start gap-2.5 px-3.5 py-2 text-left hover:bg-page">
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${item.isNew ? "bg-coral" : "bg-transparent"}`} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink truncate">{item.subject}</span>
                      <span className="block text-[11.5px] text-muted">R-{item.reportId} · {headline(item)} · {when(item.at)}</span>
                      <span className="block text-[12px] text-ink-2 leading-snug mt-0.5 line-clamp-2">{item.said}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-3.5 py-2.5 border-t border-line flex items-center gap-3 text-[11.5px]">
              <button onClick={go(onAll)} className="font-semibold text-ink-2 hover:text-ink">All your reports</button>
              <button onClick={go(onSettings)} className="ml-auto text-muted hover:text-ink">
                {news.emails ? "Emailed too · change" : "Email me these too"}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** What happened, in the words *Your reports* uses: fixed, a move, or an answer that moved nothing. */
function headline(item: ReportNewsItem): string {
  if (item.status === "fixed") return "fixed";
  if (item.status) return `moved to ${STATUS_SAID[item.status]}`;
  return "Joogoos answered";
}
